import { useEffect, useRef, useState } from "react";
import type { Lead, LeadFollowUp } from "@/types/lead";
import { insetSurfaceCardClass } from "@/components/common/surfaceClasses";
import { SalesConsoleCollapsibleSection } from "./SalesConsoleCollapsibleSection";

type FollowUpRow = LeadFollowUp & { lead: Lead; overdue: boolean };

type Props = {
  openCount: number;
  overdueCount: number;
  /** Open follow-ups due today or overdue — drives auto-expand (aligned with daily checklist alert). */
  needsAttentionCount: number;
  needsReviewCount: number;
  overdue: FollowUpRow[];
  upcoming: FollowUpRow[];
  onViewLead: (lead: Lead) => void;
  onUpdateFollowUpStatus: (leadId: string, followUpId: string, status: LeadFollowUp["status"]) => void;
};

export function SalesConsoleFollowUpSection(props: Props) {
  const [sectionOpen, setSectionOpen] = useState(() => props.needsAttentionCount > 0);
  /** User collapsed while follow-ups still needed attention — skip auto-expand until count hits zero. */
  const skippedAutoExpandUntilClearRef = useRef(false);

  useEffect(() => {
    if (props.needsAttentionCount === 0) {
      skippedAutoExpandUntilClearRef.current = false;
      return;
    }
    if (!skippedAutoExpandUntilClearRef.current) {
      setSectionOpen(true);
    }
  }, [props.needsAttentionCount]);

  function handleSectionOpenChange(next: boolean) {
    setSectionOpen(next);
    if (!next && props.needsAttentionCount > 0) {
      skippedAutoExpandUntilClearRef.current = true;
    }
  }

  return (
    <SalesConsoleCollapsibleSection
      title="Sales Console — Follow-up and disposition"
      subtitle={`Open: ${props.openCount} · Overdue: ${props.overdueCount} · Needs review: ${props.needsReviewCount}`}
      defaultOpen={false}
      open={sectionOpen}
      onOpenChange={handleSectionOpenChange}
      fireflyTargetId="sc-follow-ups"
    >
      <div className="grid gap-2 md:grid-cols-2">
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 dark:border-red-800/70 dark:bg-red-950/30">
          <p className="text-xs font-semibold text-red-900 dark:text-red-200">Overdue follow-ups</p>
          <ul className="mt-2 space-y-1 text-xs">
            {props.overdue.slice(0, 8).map((f) => (
              <li key={f.id} className={`${insetSurfaceCardClass} border-red-100 p-2 dark:border-red-900/50`}>
                <div className="font-medium text-red-900 dark:text-red-200">{f.next_action}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
                  <button type="button" className="text-blue-700 underline dark:text-blue-400" onClick={() => props.onViewLead(f.lead)}>
                    View this lead
                  </button>
                  <button type="button" className="text-emerald-700 underline dark:text-emerald-400" onClick={() => props.onUpdateFollowUpStatus(f.lead.id, f.id, "done")}>
                    Mark done
                  </button>
                </div>
              </li>
            ))}
            {props.overdue.length === 0 ? <li className="text-gray-500 dark:text-zinc-400">No overdue items.</li> : null}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800/70 dark:bg-amber-950/30">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">Today / upcoming follow-ups</p>
          <ul className="mt-2 space-y-1 text-xs">
            {props.upcoming.slice(0, 8).map((f) => (
              <li key={f.id} className={`${insetSurfaceCardClass} border-amber-100 p-2 dark:border-amber-900/50`}>
                <div className="font-medium text-amber-900 dark:text-amber-200">{f.next_action}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-600 dark:text-zinc-400">
                  <button type="button" className="text-blue-700 underline dark:text-blue-400" onClick={() => props.onViewLead(f.lead)}>
                    View this lead
                  </button>
                  <button type="button" className="text-emerald-700 underline dark:text-emerald-400" onClick={() => props.onUpdateFollowUpStatus(f.lead.id, f.id, "done")}>
                    Mark done
                  </button>
                </div>
              </li>
            ))}
            {props.upcoming.length === 0 ? <li className="text-gray-500 dark:text-zinc-400">No upcoming items.</li> : null}
          </ul>
        </div>
      </div>
    </SalesConsoleCollapsibleSection>
  );
}
